import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { CategoriesDataService, CategoriesColumnsService } from '../../services';
import { DialogCategoryFormComponent } from './category-form/category-form.component';
import { DialogCategoryDetailsComponent } from './category-details/category-details.component';
import { Category, CategoriesResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-categories',
  templateUrl: './categories.component.html',
  styleUrls: ['./categories.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class CategoriesComponent implements OnInit, OnDestroy {

  private categoryAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private categoryEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private categoryDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public categoryToDelete: Category = {};
  public categories: Category[] = [];
  public category: Category = {};
  public selectedCategories: Category[] = [];
  public submitted: boolean = false;
  public parameters: { [param: string]: string | string[] } = { offset: '0', limit: '100' };
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language) ? localStorage.language : 'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private categoriesDataService: CategoriesDataService,
    private categoriesColumnsService: CategoriesColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('CategoriesComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Categories - Management');
    this.primengConfig.ripple = true;
    this.getCategories();
  }

  ngOnDestroy(): void {
    console.log('CategoriesComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getCategories(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.categoriesDataService.getCategories(this.parameters).subscribe((response: CategoriesResult | any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.categories = response.results;
        } else {
          this.categories = this.categories.concat(response.results);
        }
        if (response.inlineCount > this.categories.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getCategories();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {
      ;
      this.isLoading = false;
      this.messageService.add({ severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('CATEGORIESNOTLOADED') });
    });
  }

  importCategories(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.categoriesDataService.addCategories(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({ severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('CATEGORIESCREATED') });
                if (proceed === j) {
                  this.getCategories();
                }
              } else {
                this.messageService.add({ severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('CATEGORIESNOTCREATED') });
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({ severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('CATEGORIESNOTCREATED') + ' ' + msg });
            });
          } else {
            this.categoriesDataService.updateCategories(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({ severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('CATEGORIESUPDATED') });
                if (proceed === j) {
                  this.getCategories();
                }
              } else {
                this.messageService.add({ severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('CATEGORIESNOTUPDATED') });
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({ severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('CATEGORIESNOTCREATED') + ' ' + msg });
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.categoriesColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
    return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
    // restore original order
    this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
    this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('categories.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedCategories() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Categories?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.categoriesDataService.deleteCategories(this.selectedCategories).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({ severity: 'success', summary: 'Successful', detail: 'Categories Deleted', life: 3000 });
            this.getCategories();
          } else {
            this.messageService.clear();
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Categories not deleted' });
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Categories not deleted' });
        });
        this.selectedCategories = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.categories, this.columns, 'Categories');
  }

  exportExcel() {
    ToolsService.exportExcel(this.categories, this.columns, 'Categories');
  }

  exportCsv() {
    ToolsService.exportCsv(this.categories, this.columns, 'Categories');
  }

  refresh() {
    this.categoriesDataService.clearCache();
    this.getCategories();
  }

  categoryAdd(): void {
    if (!this.navigation.getParamValue('categories.addInDialog', true)) {
      this.router.navigate(['/categories/add']);
      return;
    }
    this.categoryAddDialog = this.dialogService.open(DialogCategoryFormComponent, {
      header: '', // this.translate.instant('NEWCATEGORY'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        category: { id: null },
        users: this.categoriesColumnsService.users,
      },
    });

    this.categoryAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({ severity: 'info', summary: 'Success', detail: 'Category added' });
        this.getCategories();
      }
    });
  }

  categoryEdit(category: Category): void {
    if (!this.navigation.getParamValue('categories.editInDialog', true)) {
      this.router.navigate(['/categories/edit/' + category.id]);
      return;
    }
    this.categoryEditDialog = this.dialogService.open(DialogCategoryFormComponent, {
      header: '', // this.translate.instant('EDITCATEGORY') + ' ' + category.name,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        category: category,
        users: this.categoriesColumnsService.users,
      },
    });

    this.categoryEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Category updated' });
        this.getCategories();
      }
    });
  }

  categoryDetails(category: Category): void {
    if (!this.navigation.getParamValue('categories.detailsInDialog', true)) {
      this.router.navigate(['/categories/details/' + category.id]);
      return;
    }
    this.categoryDetailsDialog = this.dialogService.open(DialogCategoryDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        category: category,
      }
    });
  }

  categoryDelete(category: Category): void {
    this.categoryToDelete = category;
    this.messageService.clear();
    this.messageService.add({
      key: 'categoryDelete',
      sticky: true,
      severity: 'warn',
      summary: this.translate.instant('AREYOUSURETODELETE') + '' + category.name + ' ?',
      detail: this.translate.instant('CONFIRMTOPROCCED')
    });
  }

  onDeleteConfirm(): void {
    this.categoriesDataService.deleteCategory(this.categoryToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Category deleted' });
      this.getCategories();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Category not deleted' });
    });
  }

  onDeleteReject(): void {
    this.categoryToDelete = {};
    this.messageService.clear('categoryDelete');
  }

}
